import { OrderStatusEnum } from '@/config';
import { DriverItem } from './pickup';
import { Pickup, PickupAddress } from './pickupAddress';
import { City, RegionItem } from './region';
import { OrderStatusTab } from './columns';
import { Avatar } from './user';

interface Response {
  status: string;
  code: number;
  message: string;
}

export interface ResponseType<T> {
  status: string;
  code: number;
  message: string;
  object: T;
}
export interface ResponseListType<T> {
  status: string;
  code: number;
  message: string;
  object: {
    items: T[];
    page: number;
    total: number;
    pages: number;
    perPage: number;
  };
}
// Interface for a single order
interface Contact {
  full_name: string;
  address_text: string;
  phone: string;
  additional_phones?: string[];
  email?: string;
  lat?: string;
  lng?: string;
  region_uid: string;
}

export enum DecodeSize {
  XS = 'Extra Small',
  S = 'Small',
  M = 'Medium',
  L = 'Large',
}

// Enum for size options
type Size = 'XS' | 'S' | 'M' | 'L';

// Enum for shipment fee type
export enum ShipmentFeeType {
  BY_MERCHANT = 'BY_MERCHANT',
  BY_CUSTOMER = 'BY_CUSTOMER',
}

// Enum for payment type
export enum PaymentType {
  COLLECT_ON_DELIVERY = 'COLLECT_ON_DELIVERY',
  PREPAID = 'PREPAID',
}

// Enum for package size
export enum PackageSize {
  EXTRA_SMALL = 'extra-small',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

// Enum for pick-up type
type PickUpType = 'PICK_UP' | 'DROP_OFF';

// Interface for a single order
export interface Order {
  is_fragile?: boolean; // New field added from the JSON
  temperature_control?: boolean; // TEMP-5: cold-chain delivery flag (omitted/null = false)
  type?: 'RETURN' | 'DEFAULT';
  ready_to_pick_up?: boolean;
  shipment_fee_type?: ShipmentFeeType;
  payment_type?: PaymentType;
  pick_up_type?: PickUpType;
  description?: string;
  fee_customer_payable?: number;
  custom_id?: string;
  products?: ProductDetails[];
  contact_uid?: string;
  saved_pick_up_address_uid?: string;
  pick_up_address_uid?: string;
  size?: Size; // Restricted to 'XS', 'S', 'M', or 'L'
  contact?: Contact; // Contact details for the order
  // new check boxes
  id_check?: boolean;
  clue_verification?: boolean;
  options?: OrderOption[];
  // Phase 2: carrier selection fields
  process_id?: string;
  selected_carrier_slug?: string;
}

export interface AsyncRequestOrder {
  ready_to_pick_up: boolean;
  orders: Order[];
}

// Carrier object from API
export interface Carrier {
  uid: string;
  title: string;
  description: string;
  slug: string;
  created_at: string;
  updated_at: string; //Ship Before = updated_at
  avatar: Avatar | null;
  avatars: any[];
}

// Service type enum for carrier delivery
export enum ServiceTypeEnum {
  SAME_DAY = 'same_day',
  NEXT_DAY = 'next_day',
}

// Carrier preview item from dispatcher API
export interface CarrierPreviewItem {
  next_day_delivery: string;
  next_day_delivery_rate: string;
  average_delivery_time: string;
  shipping_fee: string;
  if_ship_before: string;
  carrier: Carrier;
  serviceType?: ServiceTypeEnum; // Added for UI display
  // TEMP-9: cold-chain capability for the destination (TEMP-8 backend).
  // Optional — the UI renders NOTHING while the backend field is absent.
  supports_temperature_control?: boolean;
}

//  response of orders
// export interface OrdersResponse {
//   status: string;
//   code: number;
//   message: string;
//   object: Order[];
// }
export interface OrdersResponse {
  status: string;
  code: number;
  message: string;
  object: {
    order: {
      pick_up_type: 'PICK_UP' | 'DROP_OFF'; // Define possible values
      payment_type: 'COLLECT_ON_DELIVERY' | 'PREPAID'; // Define possible values
      shipment_fee_type: 'BY_CUSTOMER' | 'BY_MERCHANT'; // Define possible values
      full_name: string;
      address_text: string;
      phone: string;
      additional_phones: string | null;
      description: string | null;
      custom_id: string | null;
      email: string | null;
      lat: number | null; // Assuming lat could be a number
      lng: number | null; // Assuming lng could be a number
      size: 'XS' | 'S' | 'M' | 'L'; // Define possible values for size
      is_fragile: boolean;
      temperature_control?: boolean;
      fee: number;
      platform_code: string;
      uid: string;
      pick_up_barcode: string;
      updated_at: string; // ISO 8601 date format
      created_at: string; // ISO 8601 date format
      merchant: {
        uid: string;
        first_name: string;
        last_name: string;
        email: string | null;
        phone: string;
        type: 'MERCHANT'; // Assuming this is a constant value
        verified_at: string | null; // Date format, could be null
        created_at: string; // ISO 8601 date format
        updated_at: string; // ISO 8601 date format
        deleted_at: string | null; // Date format, could be null
        avatar: string | null;
      };
    };
    preview: Preview;
  };
}

export interface OrderPriceResponse {
  status: string;
  code: number;
  message: string;
  object: Preview;
}

// Dispatcher API response for carrier preview
export interface CarrierPreviewResponse {
  status: string;
  code: number;
  message: string;
  object: {
    if_deliver_before: string;
    same_day_carriers: CarrierPreviewItem[]; //Service=Same-day
    next_day_carriers: CarrierPreviewItem[]; //Service=Next-day
  };
}

// Internal types for POST /orders/available-carriers (Phase 2 endpoint)
export interface AvailableCarrierItem {
  carrier_uid?: string; // RS-22: present so rule decisions can match by UID
  carrier_slug: string;
  carrier_name: string;
  carrier_logo_url: string;
  shipping_fee: number;
  delivery_day: 'same' | 'next';
  same_day_cutoff: string | null;
  drop_off_time: string | null;
  // TEMP-9: cold-chain capability for the destination (TEMP-8 backend adds
  // it to available-carriers). Optional — absent/undefined renders no badge.
  supports_temperature_control?: boolean;
}

export interface AvailableCarriersResponse {
  status: string;
  code: number;
  message: string;
  object: {
    process_id: string;
    recommended_carrier_slug: string;
    available_carriers: AvailableCarrierItem[];
  };
}

// Adapted response type returned by getAvailableCarriers — legacy shape + Phase 2 fields
export type AvailableCarriersAdaptedResponse = CarrierPreviewResponse & {
  process_id: string;
  recommended_carrier_slug: string;
  raw_available_carriers?: AvailableCarrierItem[];
};

export interface AvailableCarriersRequest {
  source_region_uid: string;
  destination_region_uid: string;
  type: string;
  size: string;
  products_value: number;
  is_fragile?: boolean;
  // TEMP-5: when true the backend returns only carriers eligible for
  // temperature-controlled delivery to the destination province.
  temperature_control?: boolean;
  collected_at?: string;
  options?: string[];
  // Bug E (2026-05-17): when a shipping rule restricts the carrier set,
  // forward those uids so the backend can guarantee every rule-mandated
  // carrier surfaces in the response — even those DispatchService would
  // drop for missing pricing rows. Missing-pricing entries come back with
  // shipping_fee=null + needs_pricing_quote=true.
  restricted_carrier_uids?: string[];
}

// GET /merchants/carriers/index-setting
export interface CarrierIndexSettingAttributes {
  order_size: string[];
  clue_link_verification: string;
  fragility_handling: string;
  same_day_delivery: string | null;
  // TEMP-9: cold-chain capability merged by the backend (TEMP-8). Follows the
  // fragility_handling convention (string 'true'/'false') but the UI reads it
  // tolerantly ('true' string OR boolean true = capable; absent = no badge).
  temperature_control?: string | boolean;
  // TEMPCOV — per-province cold-chain coverage codes (canonical 2-letter).
  // null/empty while temperature_control is enabled = all provinces.
  temperature_provinces?: string[] | null;
  // CHIP-1: order types this carrier accepts — 'DEFAULT' | 'RETURN' | 'EXCHANGE'.
  // A carrier declaring RETURN and/or EXCHANGE gets the Return & Exchange chip.
  order_type?: string[];
}

export interface RestrictedItemCategory {
  id: string;
  icon: string;
  title: string;
  items: string[];
}

export interface CarrierRestrictedItemsResponse {
  status: string;
  code: number;
  message: string;
  object: {
    categories: RestrictedItemCategory[];
    disclaimer: string;
    support_text: string;
  };
}

export interface CarrierIndexSettingItem {
  uid: string;
  slug: string;
  title: string;
  description: string;
  is_active: boolean;
  is_active_for_merchant: boolean;
  has_restricted_items: boolean;
  attributes: CarrierIndexSettingAttributes;
  avatar?: {
    uid: string;
    platform_code: string;
    item_type: string;
    priority: number;
    abs_url: string;
    created_at: string;
    deleted_at: string | null;
    extension: string;
    filename: string;
    type: string;
    updated_at: string;
    used_as: string;
  };
  created_at: string;
  updated_at: string;
}

export interface CarrierIndexSettingResponse {
  status: string;
  code: number;
  message: string;
  object: CarrierIndexSettingItem[];
}

// Carrier preview request body for dispatcher API
export interface CarrierPreviewRequest {
  merchant_id?: number | string | null;
  source_region_uid: string; // Pickup/Delivery Options (pickup address region)
  destination_region_uid: string; // Customer destination region
  request_time: string; // ISO timestamp or formatted time
  size: Size; // Package size: 'XS', 'S', 'M', or 'L'
  is_fragile: boolean;
  type: 'DEFAULT' | 'RETURN' | 'EXCHANGE';
  products_value: number | string;
  options?: string[]; // Array of option type strings (e.g., 'id_check', 'clue_link_verification')
}

interface PickUpAddress {
  uid: string;
  platform_code: string;
  title: string;
  type: string;
  default: number;
  full_name: string;
  phone: string;
  email: string | null;
  address_text: string;
  lat: string;
  lng: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}
//ORDER RESPONSE BY UID
export interface OrderByUIDResponse {
  status: string;
  code: number;
  message: string;
  object: {
    uid: string;
    platform_code: string;
    merchant_action_required: string | null;
    merchant_action_required_text: string | null;
    pick_up_barcode: string;
    is_fragile: number;
    temperature_control?: boolean;
    custom_id: string | null;
    region: {
      uid: string;
      platform_code: string;
      province_code: string;
      province: string;
      zaeem_area_id: number;
      area_name: string;
      remote: number;
      created_at: string;
      updated_at: string;
      deleted_at: string | null;
    };
    fee: number;
    pick_up_type: string;
    payment_type: string;
    shipment_fee_type: string;
    size: string;
    full_name: string;
    phone: string;
    pick_up_address: PickUpAddress;

    additional_phones: string | null;
    email: string;
    address_text: string;
    lat: string;
    lng: string;
    description: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    preview: Preview;
    driver: string | null;
    tracking_page: string;
    tracking_auth_page: string;
    city: {
      title: string;
      slug: string;
      deleted_at: string | null;
    };
    statuses: Array<{
      title: string;
      slug: string;
      pivot: {
        issuer_type: string;
        issuer_id: number;
        created_at: string;
        updated_at: string;
      };
    }>;
    status: {
      title: string;
      slug: string;
    };
    products: Array<{
      uid: string;
      platform_code: string;
      title: string;
      price: number;
      currency: string;
      quantity: number;
      created_at?: string;
      updated_at?: string;
      deleted_at?: string | null;
      pivot?: {
        created_at: string;
        updated_at: string;
      };
    }>;
    pick_up: string | null;
  };
}

// order list status
export interface OrderListResponse {
  status: string;
  code: number;
  message: string;
  object: {
    items: OrderItem[];
    page: number;
    total: number;
    pages: number;
    perPage: number;
  };
}

export interface OrderSingleResponse {
  status: string;
  code: number;
  message: string;
  object: SingleOrderItem;
}
type StatusCounts<T extends string> = {
  [K in T | 'total']: number;
};
type FinishedKeys =
  | 'delivered'
  | 'partially_delivered'
  | 'returned'
  | 'partially_returned'
  | 'resend'
  | 'cancelled';

type InProgressKeys =
  | 'collecting'
  | 'collected'
  | 'in_transit'
  | 'out_for_delivery'
  | 'postponed'
  | 'hold_resolved';

type OnHoldKeys = 'action_required' | 'on_hold' | 'retry_request' | 'exception';

type ReadyToShipKeys = 'new' | 'scheduled';

type ReturnToOriginKeys =
  | 'returned_warehouse'
  | 'returning_origin'
  | 'rto_requested'
  | 'rto_scheduled';

export interface OrderStatusCountResponse {
  [OrderStatusTab.FINISHED]: StatusCounts<FinishedKeys>;
  [OrderStatusTab.IN_PROGRESS]: StatusCounts<InProgressKeys>;
  [OrderStatusTab.ON_HOLD]: StatusCounts<OnHoldKeys>;
  [OrderStatusTab.READY_TO_SHIP]: StatusCounts<ReadyToShipKeys>;
  [OrderStatusTab.RTO]: StatusCounts<ReturnToOriginKeys>;
  [OrderStatusTab.INVENTORY]?: StatusCounts<'inventory'>;
}

export type SubTabOrderKey =
  | FinishedKeys
  | InProgressKeys
  | OnHoldKeys
  | ReadyToShipKeys
  | ReturnToOriginKeys;

export interface OrderTransaction {
  status: TransactionStatus;
  amount: number;
  created_at: string;
  details: string;
  is_correction: 1 | 0;
  item_platform_code: string;
  item_uid: string;
  platform_code: string;
  subject: string;
  type: string;
  uid: string;
  remittance: TransactionRemittance | null;
}

interface TransactionRemittance {
  uid: string;
  platform_code: string;
  amount: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface TransactionStatus {
  title: string;
  slug: TransactionStatusEnum;
}

export enum TransactionStatusEnum {
  ENABLED = 'enabled',
  CANCEL = 'cancel',
  PENDING = 'pending',
  PAID = 'paid',
}

export interface SingleOrderItem {
  uid: string;
  platform_code: string;
  rto_id: string | null;
  merchant_action_required?: number;
  merchant_action_required_text: string | null;
  merchant_action_required_merchant_text: string | null;
  pick_up_barcode: string;
  is_fragile: number;
  temperature_control?: boolean;
  custom_id: string;
  region: RegionItem;
  fee: number;
  pick_up_type: string;
  payment_type: PaymentType;
  shipment_fee_type: ShipmentFeeType;
  size: string;
  full_name: string;
  phone: string;
  secondary_phone: string;
  additional_phones: string | null;
  email: string | null;
  address_text: string;
  lat: string | null;
  lng: string | null;
  description: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  preview: Preview;
  driver: DriverItem | null;
  tracking_page: string;
  tracking_auth_page: string;
  city: City;
  statuses: ShipmentStatus[];
  status: ShipmentStatus;
  // True when the order was collected via a BULK pickup (see OrderItem note).
  is_bulk_collected?: boolean;
  // True once a merchant generated a shipping label for this order (one-way).
  label_printed?: boolean;
  products: ProductDetails[];
  pick_up: Pickup | null;
  saved_pick_up_address?: PickupAddress | null;
  fee_customer_payable: number;
  remittance: OrderRemittance | null;
  carrier_message: string | null;
  type: 'DEFAULT' | 'RETURN' | 'EXCHANGE';
  options?: OrderOption[];
  discount_amount: number | null;
  prepayment_amount: number | null;
  carrier: Carrier | null;
  parent_order_id: number | null;
  parent_order_uid: string | null;
  parent_order: { uid: string; platform_code: string } | null;
  resend_orders: Array<{ uid: string; platform_code: string; status: { slug: string; title: string } }>;
  claim?: {
    uid: string;
    platform_code: string;
    type: string;
    declared_value: number;
    compensation_amount: number | null;
    status: { slug: string; title: string };
    created_at: string;
  } | null;
}

export enum OrderOptionEnum {
  ID_CHECK = 'id_check',
  CLUE_VERIFICATION = 'clue_link_verification',
}

export interface OrderOption {
  type: OrderOptionEnum;
  value?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

export interface OrderRemittance {
  platform_code: string;
  uid: string;
  status: Status;
  created_at: string;
}

export interface ShipmentStatus {
  title: string;
  slug: string;
  merchant_title: string;
  pivot: ProductDetailsPivot;
}

interface ProductDetailsPivot {
  issuer_type?: string;
  issuer_id?: number;
  created_at: string;
  updated_at: string;
}

export interface Preview {
  orders_value: number;
  orders_shipment_fee: number;
  orders_value_with_shipment_fee: number;
  orders_value_with_platform_shipment_fee: number;
  orders_custom_shipment_fee: number;
}
export interface AsyncPreviewResponse {
  orders: AsyncRequestOrder;
  total: Preview;
}
export interface OrderItem {
  uid: string;
  platform_code: string;
  merchant_action_required: number;
  merchant_action_required_text: string | null;
  carrier_message: string | null;
  pick_up_barcode: string;
  is_fragile: number;
  temperature_control?: boolean;
  custom_id: string;
  region: string | null;
  fee: number;
  pick_up_type: string;
  payment_type: string;
  shipment_fee_type: string;
  products_value: number;
  size: string;
  full_name: string;
  phone: string;
  additional_phones: string[];
  email: string;
  address_text: string;
  lat: string;
  lng: string;
  description: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  status: Status;
  // True when the order was collected via a BULK pickup. The order's own
  // status.slug stays `collected` — `bulk-collected` is NOT an order status —
  // so the UI shows "Bulk Collected" only while flag is true AND slug=collected.
  is_bulk_collected?: boolean;
  // True once a merchant generated a shipping label for this order (one-way).
  label_printed?: boolean;
  pick_up?: Pickup | null;
  saved_pick_up_address?: PickupAddress | null;
  products: ProductDetails[];
  driver?: DriverItem | null;
  carrier: Carrier | null;
}

export interface ProductPivot {
  order_id: number;
  product_id: number;
  created_at: string;
  updated_at: string;
}

export interface BasicProduct {
  title: string;
  price: number | string;
  quantity?: number | string;
  uid?: string | null;
  product_catalog_uid?: string;
  is_fragile?: boolean;
}

export interface ProductDetails extends BasicProduct {
  currency?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  pivot?: ProductDetailsPivot;
  platform_code?: string;
  uid?: string | null;
  status?: ProductStatus;
}

export interface ProductStatus {
  title: string;
  slug: string;
}

export interface Status {
  merchant_title: string;
  title: string;
  slug: OrderStatusEnum;
}

export interface OrdersRow {
  key: string | undefined;
  pickupAddressId: string | undefined;
  platform_code: string | undefined;
  total: number;
  status?: string;
  merchant_action_required?: number;
}

//Resolve action and response:
interface ResolvedObject {
  id: string;
  name: string;
  description: string;
  // Add other fields relevant to the object structure
}
export interface OrderResolveResponse {
  status: string;
  code: number;
  message: string;
  object: ResolvedObject[]; // Use the specific type instead of `any[]`
}
export interface OrderResolveAction {
  merchant_action_required_merchant_text: string;
}

//RTO request :
export interface OrderRtoRequest {
  merchant_action_required_merchant_text: string;
}
export enum PickupTypeEnum {
  COD = 'COD_DROP_OFF_ADDRESS',
  WAREHOUSE = 'WAREHOUSE',
}
export type OrderColumn = {
  customerAddress: string;
  customerName: string;
  key: string;
  orderDate: string;
  orderId: string;
  pickupAddressId: string;
  pickupLocation: string;
  products: ProductDetails[];
  status: string;
};

export interface OrderSingleRtoResponse {
  status: string;
  code: number;
  message: string;
  object: SingleRTO;
}

export interface OrderRtoResponse {
  status: string;
  code: number;
  message: string;
  object: {
    items: SingleRTO[];
    page: number;
    total: number;
    pages: number;
    perPage: number;
  };
}

export interface SingleRTO {
  uid: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  orders_count: number;
  shipment_fee: number;
  order_value: number | null;
  driver: DriverItem | null;
  total_price: number;
  pick_up_address: PickUpAddress;
  order: SingleOrderItem;
  order_current_status: Status;
  task: null;
}

export interface ActivityLogResponse extends Response {
  object: {
    items: ActivityItem[];
  };
}

export interface ActivityItem {
  description?: string;
  title: string;
  created_at: string;
  updated_at?: string;
  deleted_at?: string;
  images?: ImageItem[];
}

interface ImageItem {
  // it probably should be changed
  imageTitle: string;
  imageUrl: string;
}

export interface StatusAnnotation {
  created_at: string;
  status: Status;
  text: string | null;
  status_annotation_tag: {
    title: string | null;
    note: string | null;
  };
}

export interface OrderStatusTabConfig {
  key: OrderStatusTab;
  titleKey: string;
  hasContent?: boolean;
  hasBadge?: boolean;
}

export interface OrderProofOfDelivery {
  proof: string[] | string | null;
}
